import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  data?: any;
  code?: number;    // 后端使用 code 标识状态
  message?: string; // 后端使用 message 标识消息
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType, code, message: msg } =
        res as unknown as ResponseStructure;
      
      // 这里的 success 是可选的，后端 cooking-api 统一使用 code: 0 表示成功
      // 我们优先使用 success，如果不存在则使用 code === 0 判断
      const isSuccess = success ?? (code === 0);
      
      if (!isSuccess) {
        const error: any = new Error(errorMessage || msg || '请求失败');
        error.name = 'BizError';
        error.info = { 
          errorCode: errorCode ?? code, 
          errorMessage: errorMessage || msg, 
          showType, 
          data 
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // 尝试读取响应体中的 message 字段
        const responseData = error.response.data as ResponseStructure;
        const errorMessage =
          responseData?.message || `Response status: ${error.response.status}`;

        // 将错误信息附加到 error 对象，便于调用方使用
        error.data = responseData;
        error.message = errorMessage;

        // 不在这里显示消息，让调用方决定如何处理
        throw error;
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        error.message = 'None response! Please retry.';
        throw error;
      } else {
        // 发送请求时出了点问题
        error.message = 'Request error, please retry.';
        throw error;
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，添加认证token
      const token = localStorage.getItem('access_token');
      const tokenType = localStorage.getItem('token_type') || 'Bearer';

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `${tokenType} ${token}`,
        };
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      // cooking-api 的响应格式：{ code: 0, message: 'success', data: {} }
      // 注意：这里 response 是 AxiosResponse，后端返回的数据在 response.data 中
      const res = response.data as ResponseStructure;

      // 如果响应中有code字段，判断是否成功
      if (res?.code !== undefined && res.code !== 0) {
        // 401 未授权，需要重新登录（但不是在登录页面时）
        if (
          res.code === 401 &&
          !window.location.pathname.includes('/user/login')
        ) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_type');
          window.location.href = '/user/login';
          return response;
        }

        // 抛出错误，让调用方处理，避免重复提示
        const error: any = new Error(res.message || '请求失败');
        error.response = response;
        error.data = res;
        throw error;
      }

      return response;
    },
  ],
};
